import React from "react";
import isEqual from "lodash/isEqual";
import { withTranslation } from "react-i18next";

const Chartist = process.browser && require("chartist");

const BASE_OPTIONS = {
  donut: true,
  donutSolid: true,
  donutWidth: "15%",
  height: 250,
  ignoreEmptyValues: false,
  showLabel: false,
  width: "100%",
};

class NutrientsChart extends React.Component {
  constructor(props) {
    super(props);
  }

  componentDidMount() {
    require("chartist-plugin-fill-donut");
    const { data, donutContent } = this.generateChartData();
    const { height, providedId, width } = this.props;
    if (height) {
      BASE_OPTIONS.height = height;
    }
    if (width) {
      BASE_OPTIONS.width = width;
    }

    const options = Object.assign({}, BASE_OPTIONS, {
      plugins: [
        Chartist.plugins.fillDonut({
          items: [
            {
              content: donutContent,
            },
          ],
        }),
      ],
    });

    this.chartObject = new Chartist.Pie(
      `#${providedId || "donut-chart"}`,
      data,
      options
    );
  }

  componentDidUpdate(prevProps) {
    if (
      !isEqual(prevProps.dayStats, this.props.dayStats) ||
      !isEqual(prevProps.targetData, this.props.targetData)
    ) {
      this.updateChart();
    }
  }

  getNutrientsData() {
    const { dayStats, targetData } = this.props;
    const { carbs = 0, fats = 0, proteins = 0, fibers = 0 } = dayStats.nutrients;
    const proteinsDays = dayStats.proteinsDays || 1;
    const carbsDays = dayStats.carbsDays || 1;
    const fatsDays = dayStats.fatsDays || 1;
    const fibersDays = dayStats.fibersDays || 1;
    const targetCalories = targetData
      ? Math.round(targetData.targetCalories) || 0
      : 0;

    const proteinsKcal = Math.round((proteins * 4) / proteinsDays);
    const carbsKcal = Math.round((carbs * 4) / carbsDays);
    const fatsKcal = Math.round((fats * 9) / fatsDays);
    const calories = Math.round(proteinsKcal + carbsKcal + fatsKcal);
    const caloriesLeft = Math.round(targetCalories - calories);

    const proteinPercent = Math.round((proteinsKcal / calories) * 100) || 0;
    const carbsPercent = Math.floor((carbsKcal / calories) * 100) || 0;
    const fatPercent = Math.round((fatsKcal / calories) * 100) || 0;

    // Grams (not kcal-derived) — only needed by the detailedLegend format,
    // which shows "185g · 30%" instead of just the percent.
    const proteinsGrams = Math.round(proteins / proteinsDays);
    const carbsGrams = Math.round(carbs / carbsDays);
    const fatsGrams = Math.round(fats / fatsDays);
    const fibersGrams = Math.round(fibers / fibersDays);

    return {
      proteinsKcal,
      carbsKcal,
      fatsKcal,
      calories,
      caloriesLeft,
      proteinPercent,
      carbsPercent,
      fatPercent,
      proteinsGrams,
      carbsGrams,
      fatsGrams,
      fibersGrams,
    };
  }

  generateChartData() {
    const { targetData, t } = this.props;
    const { proteinsKcal, carbsKcal, fatsKcal, calories, caloriesLeft } =
      this.getNutrientsData();

    const data = {
      series: [
        { value: proteinsKcal, className: "proteins" },
        { value: carbsKcal, className: "carbs" },
        { value: fatsKcal, className: "fats" },
      ],
    };

    const overLabel = t("nutritionPage.donut.over");
    const leftLabel = t("nutritionPage.donut.left");
    const kcalLabel = t("nutritionPage.donut.kcal");

    const targetCalories =
      targetData && targetData.targetCalories
        ? `<span class="kcal-target">/${Math.round(
            targetData.targetCalories
          )}</span>`
        : "";
    const left =
      targetData && targetData.targetCalories
        ? `
          <div>
            <span class="kcal-left${caloriesLeft < 0 ? " over" : ""}">
              ${Math.abs(caloriesLeft)} ${caloriesLeft < 0 ? overLabel : leftLabel}
            </span>
          </div>
        `
        : "";

    const donutContent = `
    <div class="donut-content">
      <div>
        <span class="kcal-value">${calories}</span>
        ${targetCalories}
      </div>

      <div class="kcal-label">${kcalLabel}</div>
      ${left}
    </div>`;

    return { data, donutContent };
  }

  updateChart() {
    const { data, donutContent } = this.generateChartData();
    const options = Object.assign({}, BASE_OPTIONS, {
      fillDonutOptions: { items: [{ content: donutContent }] },
    });
    this.chartObject.update(data, options);
  }

  renderLegend() {
    const { t, detailedLegend } = this.props;
    const {
      proteinPercent,
      carbsPercent,
      fatPercent,
      proteinsGrams,
      carbsGrams,
      fatsGrams,
      fibersGrams,
    } = this.getNutrientsData();

    // Opt-in format ("185g · 30%", colored labels, + a gram-only Fibre row)
    // requested for the Nutrition Plans tab's plan card — left as opt-in
    // rather than the default so this shared component's other 8 consumers
    // (AI-generate modals, calendar log card, recipe editor, ...) keep their
    // existing percent-only legend untouched.
    if (detailedLegend) {
      return (
        <div className="legend-container legend-container--detailed">
          <div className="legend-row">
            <span className="legend-label protein">{t("nutritionPage.progress.macros.proteins")}</span>
            <span className="legend-value">{proteinsGrams}g · {proteinPercent}%</span>
          </div>
          <div className="legend-row">
            <span className="legend-label carbs">{t("nutritionPage.progress.macros.carbs")}</span>
            <span className="legend-value">{carbsGrams}g · {carbsPercent}%</span>
          </div>
          <div className="legend-row">
            <span className="legend-label fat">{t("nutritionPage.progress.macros.fats")}</span>
            <span className="legend-value">{fatsGrams}g · {fatPercent}%</span>
          </div>
          {!!fibersGrams && (
            <div className="legend-row">
              <span className="legend-label fibre">{t("nutritionPage.progress.macros.fibers")}</span>
              <span className="legend-value">{fibersGrams}g</span>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="legend-container">
        {!!proteinPercent && (
          <div className="legend-item">
            <div className="legend-indicator protein" />
            <span>{t("nutritionPage.donut.proteinPercent", { percent: proteinPercent })}</span>
          </div>
        )}
        {!!carbsPercent && (
          <div className="legend-item">
            <div className="legend-indicator carbs" />
            <span>{t("nutritionPage.donut.carbsPercent", { percent: carbsPercent })}</span>
          </div>
        )}
        {!!fatPercent && (
          <div className="legend-item">
            <div className="legend-indicator fat" />
            <span>{t("nutritionPage.donut.fatPercent", { percent: fatPercent })}</span>
          </div>
        )}
      </div>
    );
  }

  render() {
    return (
      <div className="graph-container donut">
        <div
          className="donut-chart"
          id={this.props.providedId || "donut-chart"}
        />
        {this.renderLegend()}
      </div>
    );
  }
}

export default withTranslation("athlete-portal")(NutrientsChart);
